import React, { useMemo, useState } from 'react'
import { Bar, Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import {
  PerformancePeriodData,
  PipelinePeriodData,
} from '../../services/recommendations_api'
import { formatCurrency } from '../../utils/formatting'
import { PillTabs } from '../FbUI/PillTabs'

// Register Chart.js components (required before using Bar/Line charts)
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
)

/**
 * Performance-chart palette — Brand Basil scale, same idea as
 * `companyPageChartColors.ts`. Related metric pairs (opened/won, sent/received)
 * are different shades of basil so the chart reads as a single coherent
 * brand-colored visualization. Loss/negative gets the only non-basil accent.
 *
 * Datalabels render above the bars in dark text rather than on top of the bar
 * fill, so the bar shade itself isn't bound to a WCAG-AA contrast against
 * white text — it can be as bright as Brand Basil 400/500/600.
 */
const CHART_COLORS = {
  DEALS_OPENED: '#0A9150', // basil-600 — primary action
  DEALS_WON: '#39CE7E', // basil-400 — positive outcome, lighter
  DEALS_LOST: '#EF4444', // red-500 — bright red, the only non-basil accent
  EMAILS_SENT: '#0A9150', // basil-600 — outbound
  EMAILS_RECEIVED: '#72E3A3', // basil-300 — inbound, lighter
  VALUE_OPENED: '#39CE7E', // basil-400 — value at risk
  VALUE_WON: '#0A9150', // basil-600 — value secured
  WEIGHTED_VALUE: '#72E3A3', // basil-300 — forecast, softer
} as const

/** Shared chart plugin config for legend and tooltip base styling. */
const SHARED_CHART_PLUGINS = {
  legend: {
    position: 'bottom' as const,
    labels: {
      usePointStyle: true,
      boxWidth: 8,
      boxPadding: 10,
      padding: 20,
      font: { size: 12 },
    },
  },
  tooltip: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    padding: 12,
    titleFont: { size: 12 },
    bodyFont: { size: 12 },
    position: 'nearest' as const,
    // Force the tooltip body to sit *below* its anchor (caret on top), so it
    // can never sit on top of the datalabel that lives above each bar.
    yAlign: 'top' as const,
    caretPadding: 8,
    // Hide tooltip lines where value is 0 to reduce clutter
    filter: (tooltipItem: { raw: unknown }) =>
      (tooltipItem.raw as number) !== 0,
  },
  // Datalabels plugin is globally registered — configure it to be clean:
  // hide 0 values and render labels above the bars in dark text. Sitting on
  // the white card background (not on the bar) lets the bar shade be any
  // brightness without breaking label contrast.
  datalabels: {
    display: ((context: any) =>
      ((context.dataset.data[context.dataIndex] as number) ?? 0) >
      0) as unknown as boolean,
    color: '#1F2937', // gray-800 — high contrast on white card
    font: { size: 12, weight: 'bold' as const },
    anchor: 'end' as const,
    align: 'end' as const,
    offset: 4,
  },
} as const

/** Max pixel width for a single bar to prevent overlap when many periods. */
const MAX_BAR_THICKNESS = 32

/** Available tabs for the performance chart. */
type ChartTab = 'deals' | 'emails' | 'value' | 'pipeline'

interface PerformanceChartProps {
  /** Array of period data to display, ordered newest first from API. */
  periods: PerformancePeriodData[]
  /** Pipeline period data for the Pipeline tab. */
  pipelinePeriods?: PipelinePeriodData[]
  /** Optional additional CSS class names. */
  className?: string
}

/**
 * Grouped bar chart showing performance metrics over time.
 *
 * Features:
 * - Tabs to switch between Deals, Emails, and Value views
 * - Grouped bar chart for each category
 * - Tooltips show relevant context (win rate for deals, etc.)
 *
 * Color scheme:
 * - Deals opened: Dark Green (primary metric)
 * - Deals won: Light Green (success indicator)
 * - Deals lost: Red (failure indicator)
 * - Emails sent: Cyan
 * - Value opened: Blue
 * - Value won: Light Green
 */
export const PerformanceChart: React.FC<PerformanceChartProps> = ({
  periods,
  pipelinePeriods,
  className,
}) => {
  const [activeTab, setActiveTab] = useState<ChartTab>('deals')
  const tabOptions = useMemo(
    () => [
      { key: 'deals' as ChartTab, title: 'Deals' },
      { key: 'emails' as ChartTab, title: 'Emails' },
      { key: 'value' as ChartTab, title: 'Value' },
      ...(pipelinePeriods && pipelinePeriods.length > 0
        ? [{ key: 'pipeline' as ChartTab, title: 'Pipeline' }]
        : []),
    ],
    [pipelinePeriods]
  )

  // Reverse to show oldest to newest (left to right)
  const sortedPeriods = useMemo(() => [...periods].reverse(), [periods])
  const labels = useMemo(
    () => sortedPeriods.map((p) => p.period_label),
    [sortedPeriods]
  )

  const dealsChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Deals Opened',
          data: sortedPeriods.map((p) => p.deals_opened),
          backgroundColor: CHART_COLORS.DEALS_OPENED,
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Deals Won',
          data: sortedPeriods.map((p) => p.deals_won),
          backgroundColor: CHART_COLORS.DEALS_WON,
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Deals Lost',
          data: sortedPeriods.map((p) => p.deals_lost),
          backgroundColor: CHART_COLORS.DEALS_LOST,
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  const emailsChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Emails Sent',
          data: sortedPeriods.map((p) => p.emails_sent),
          backgroundColor: CHART_COLORS.EMAILS_SENT,
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Emails Received',
          data: sortedPeriods.map((p) => p.emails_received),
          backgroundColor: CHART_COLORS.EMAILS_RECEIVED,
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  const valueChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Value Opened',
          data: sortedPeriods.map((p) => p.value_opened),
          backgroundColor: CHART_COLORS.VALUE_OPENED,
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
        {
          label: 'Value Won',
          data: sortedPeriods.map((p) => p.value_won),
          backgroundColor: CHART_COLORS.VALUE_WON,
          borderRadius: 3,
          maxBarThickness: MAX_BAR_THICKNESS,
        },
      ],
    }),
    [labels, sortedPeriods]
  )

  // Pipeline chart data (Line chart with dual y-axes)
  const sortedPipelinePeriods = useMemo(
    () => (pipelinePeriods ? [...pipelinePeriods].reverse() : []),
    [pipelinePeriods]
  )
  const pipelineLabels = useMemo(
    () => sortedPipelinePeriods.map((p) => p.period_label),
    [sortedPipelinePeriods]
  )

  const pipelineChartData = useMemo(
    () => ({
      labels: pipelineLabels,
      datasets: [
        {
          label: 'Unweighted Value',
          data: sortedPipelinePeriods.map((p) => p.unweighted_value),
          borderColor: CHART_COLORS.VALUE_OPENED,
          backgroundColor: CHART_COLORS.VALUE_OPENED,
          yAxisID: 'y',
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: 'Weighted Value',
          data: sortedPipelinePeriods.map((p) => p.weighted_value),
          borderColor: CHART_COLORS.WEIGHTED_VALUE,
          backgroundColor: CHART_COLORS.WEIGHTED_VALUE,
          yAxisID: 'y',
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: 'Deal Count',
          data: sortedPipelinePeriods.map((p) => p.deal_count),
          borderColor: CHART_COLORS.DEALS_OPENED,
          backgroundColor: CHART_COLORS.DEALS_OPENED,
          yAxisID: 'y1',
          tension: 0.3,
          pointRadius: 3,
        },
      ],
    }),
    [pipelineLabels, sortedPipelinePeriods]
  )

  const getBarChartData = () => {
    switch (activeTab) {
      case 'deals':
        return dealsChartData
      case 'emails':
        return emailsChartData
      case 'value':
        return valueChartData
      default:
        return dealsChartData
    }
  }

  const barOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 28, right: 16, bottom: 8, left: 8 },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        ...SHARED_CHART_PLUGINS,
        datalabels: {
          ...SHARED_CHART_PLUGINS.datalabels,
          ...(activeTab === 'value'
            ? {
                formatter: (value: number) =>
                  formatCurrency(value, { decimals: false }),
                font: { size: 12, weight: 'bold' as const },
              }
            : {}),
        },
        tooltip: {
          ...SHARED_CHART_PLUGINS.tooltip,
          callbacks:
            activeTab === 'deals'
              ? {
                  afterBody: (tooltipItems) => {
                    const periodIndex = tooltipItems[0]?.dataIndex
                    if (periodIndex !== undefined) {
                      const period = sortedPeriods[periodIndex]
                      if (period?.win_rate !== null) {
                        return `\nWin Rate: ${period.win_rate}%`
                      }
                    }
                    return ''
                  },
                }
              : activeTab === 'value'
                ? {
                    label: (context) => {
                      const value = context.raw as number
                      return `${context.dataset.label}: ${formatCurrency(value)}`
                    },
                  }
                : undefined,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: { size: 12 },
            padding: 8,
            autoSkip: true,
            maxRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text:
              activeTab === 'deals'
                ? 'Deals'
                : activeTab === 'emails'
                  ? 'Emails'
                  : 'Value ($)',
            font: { size: 12 },
            padding: { bottom: 12 },
          },
          ticks: {
            font: { size: 12 },
            padding: 8,
            precision: 0,
            callback:
              activeTab === 'value'
                ? (value) =>
                    formatCurrency(value as number, { decimals: false })
                : undefined,
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
    }),
    [activeTab, sortedPeriods]
  )

  const pipelineOptions: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 28, right: 16, bottom: 8, left: 8 },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        ...SHARED_CHART_PLUGINS,
        datalabels: { display: false },
        tooltip: {
          ...SHARED_CHART_PLUGINS.tooltip,
          callbacks: {
            label: (context) => {
              const value = context.raw as number
              if (context.dataset.yAxisID === 'y1') {
                return `${context.dataset.label}: ${value}`
              }
              return `${context.dataset.label}: ${formatCurrency(value)}`
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 12 }, padding: 8 },
        },
        y: {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          title: {
            display: true,
            text: 'Value ($)',
            font: { size: 12 },
            padding: { bottom: 12 },
          },
          ticks: {
            font: { size: 12 },
            padding: 8,
            callback: (value) =>
              formatCurrency(value as number, { decimals: false }),
          },
          grid: { color: 'rgba(0, 0, 0, 0.05)' },
        },
        y1: {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: {
            display: true,
            text: 'Deals',
            font: { size: 12 },
            padding: { bottom: 12 },
          },
          ticks: {
            font: { size: 12 },
            padding: 8,
            precision: 0,
          },
          grid: { drawOnChartArea: false },
        },
      },
    }),
    []
  )

  if (
    periods.length === 0 &&
    (!pipelinePeriods || pipelinePeriods.length === 0)
  ) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-48 text-fg-secondary text-sm">
          No data available for the selected period
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Tab buttons — pill style: these tabs filter the chart between
          metrics (Deals / Emails / Value / Pipeline), not different
          content views, so they read as a segmented filter control. */}
      <div className="mb-3">
        <PillTabs<ChartTab>
          tabs={tabOptions}
          activeKey={activeTab}
          onChange={setActiveTab}
          idPrefix="performance-chart"
          aria-label="Performance metric"
          className="w-fit"
        />
      </div>

      {/* Chart */}
      <div className="h-72 relative">
        {activeTab === 'pipeline' ? (
          <Line data={pipelineChartData} options={pipelineOptions} />
        ) : (
          <Bar data={getBarChartData()} options={barOptions} />
        )}
      </div>
    </div>
  )
}
